import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Mail } from 'lucide-react';
import Swal from 'sweetalert2';
import { Banner } from '../../../components/common/Banner';
import SectionTips from '../../../components/common/SectionTips';
import TooltipHelper from '../../../components/common/TooltipHelper';
import { getMembershipConfigs, updateMembershipConfig } from '../../../services/membershipConfigService';

const btn = "inline-flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap";
const btnPri = `${btn} bg-[#0488AF] text-white hover:bg-[#037a98] disabled:opacity-60 disabled:cursor-not-allowed`;
const input = "w-full rounded-2xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white";

const ChipTitle = ({ children }) => (
  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
    <span className="inline-block w-1.5 h-5 bg-[#0488AF] rounded-full" />
    {children}
  </div>
);

function GradientCard({ children }) {
  return (
    <div
      className="rounded-2xl p-[1.5px]"
      style={{ background: "linear-gradient(135deg,#E6F0FF 0%,#E6FBFF 100%)" }}
    >
      <div className="rounded-2xl bg-white ring-1 ring-black/5">{children}</div>
    </div>
  );
}

export default function MembershipConfigPage() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [editedValues, setEditedValues] = useState({});

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await getMembershipConfigs();
      if (response.ok && response.data) {
        const configsData = response.data;
        setConfigs(configsData);
        // Inicializar valores editados
        const initialValues = {};
        configsData.forEach(config => {
          initialValues[config.key] = config.value || '';
        });
        setEditedValues(initialValues);
      }
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al cargar configuraciones', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (key, value) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validateEmailList = (value) => {
    if (!value || value.trim() === '') return true; // Permitir vacío
    const emails = value.split(',').map(e => e.trim()).filter(Boolean);
    return emails.every(email => validateEmail(email));
  };

  const handleSave = async (config) => {
    const newValue = editedValues[config.key] || '';
    
    // Validar emails según el tipo de configuración
    if (config.key === 'MEMBERSHIP_REPLY_TO') {
      if (newValue.trim() && !validateEmail(newValue.trim())) {
        Swal.fire('Error', 'Por favor ingresa un email válido', 'error');
        return;
      }
    } else if (config.key === 'MEMBERSHIP_NOTIFY_TO' || config.key === 'MEMBERSHIP_CC_DEFAULT') {
      if (newValue.trim() && !validateEmailList(newValue)) {
        Swal.fire('Error', 'Por favor ingresa emails válidos separados por comas', 'error');
        return;
      }
    }

    try {
      setSaving(prev => ({ ...prev, [config.key]: true }));
      await updateMembershipConfig(config.key, newValue.trim(), config.description);
      
      await Swal.fire({
        icon: 'success',
        title: 'Configuración actualizada',
        text: 'La configuración se guardó correctamente',
        timer: 1500,
        showConfirmButton: false,
      });

      await loadConfigs();
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al actualizar configuración', 'error');
    } finally {
      setSaving(prev => ({ ...prev, [config.key]: false }));
    }
  };

  const getConfigLabel = (key) => {
    const labels = {
      'MEMBERSHIP_NOTIFY_TO': 'Email de Notificación',
      'MEMBERSHIP_REPLY_TO': 'Email de Respuesta (Reply-To)',
      'MEMBERSHIP_CC_DEFAULT': 'Emails en CC por Defecto',
    };
    return labels[key] || key;
  };

  const getConfigPlaceholder = (key) => {
    const placeholders = {
      'MEMBERSHIP_NOTIFY_TO': 'ejemplo@cidere.cl, otro@cidere.cl',
      'MEMBERSHIP_REPLY_TO': 'respuesta@cidere.cl',
      'MEMBERSHIP_CC_DEFAULT': 'cc1@cidere.cl, cc2@cidere.cl',
    };
    return placeholders[key] || '';
  };

  const emailConfigs = configs.filter(config => 
    ['MEMBERSHIP_NOTIFY_TO', 'MEMBERSHIP_REPLY_TO', 'MEMBERSHIP_CC_DEFAULT'].includes(config.key)
  );

  return (
    <div className="w-full overflow-x-hidden">
      <div className="max-w-screen-xl mx-auto px-4 py-6 space-y-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-3 px-3 py-2 bg-[#DC3465] hover:bg-[#c02e59] rounded-xl text-white"
        >
          <ArrowLeft size={18} /> Volver
        </button>

        <Banner
          title="Configuración de Correos de Membresía"
          message="Gestiona las direcciones de correo utilizadas en los emails de membresía. Puedes dejar campos vacíos si no deseas usarlos."
        />

        <SectionTips
          section="la configuración de correos de membresía"
          buttonTitle="¿Cómo usar esta sección?"
          tipsTitle="¿Cómo gestionar la configuración de correos?"
          tipsList={[
            '<strong>Email de Notificación:</strong> Direcciones que recibirán notificaciones cuando se cree una nueva postulación de membresía. Separa múltiples emails con comas.',
            '<strong>Email de Respuesta (Reply-To):</strong> Dirección que aparecerá como "Responder a" en los correos enviados. Debe ser un único email válido.',
            '<strong>Emails en CC por Defecto:</strong> Direcciones que se agregarán automáticamente en copia (CC) cuando se envíen correos de membresía. Separa múltiples emails con comas.',
            'Todos los campos son opcionales. Si dejas un campo vacío, no se utilizará esa funcionalidad.',
            'Los cambios se aplicarán inmediatamente a los nuevos correos enviados.',
          ]}
          finalNote="Consejo: Asegúrate de que los emails sean válidos y estén activos para recibir las notificaciones correctamente."
          color="#0488AF"
          videoUrl=""
          manualUrl=""
        />

        {loading ? (
          <div className="text-center py-12 text-gray-500">
            <Loader2 className="animate-spin mx-auto mb-2" size={24} />
            Cargando configuraciones...
          </div>
        ) : (
          <GradientCard>
            <div className="p-5">
              <ChipTitle>Configuración de Correos</ChipTitle>
              <div className="mt-4 space-y-4">
                {emailConfigs.length > 0 ? (
                  emailConfigs.map((config) => (
                    <div key={config.id} className="p-4 rounded-xl border bg-gray-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Mail size={16} className="text-[#0488AF]" />
                            <label className="text-sm font-semibold text-gray-900">
                              {getConfigLabel(config.key)}
                            </label>
                            <TooltipHelper text={
                              config.key === 'MEMBERSHIP_NOTIFY_TO' || config.key === 'MEMBERSHIP_CC_DEFAULT'
                                ? `Separa múltiples emails con comas. ${(config.description || `Configuración de ${getConfigLabel(config.key)}`).replace(/punto y coma/gi, 'comas')}`
                                : (config.description || `Configuración de ${getConfigLabel(config.key)}`).replace(/punto y coma/gi, 'comas')
                            } />
                          </div>
                          <input
                            type="text"
                            value={editedValues[config.key] || ''}
                            onChange={(e) => handleValueChange(config.key, e.target.value)}
                            className={input}
                            placeholder={getConfigPlaceholder(config.key)}
                            disabled={saving[config.key]}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {config.key === 'MEMBERSHIP_NOTIFY_TO' || config.key === 'MEMBERSHIP_CC_DEFAULT'
                              ? `Separa múltiples emails con comas. ${(config.description || `Configuración de ${getConfigLabel(config.key)}`).replace(/punto y coma/gi, 'comas')}`
                              : (config.description || `Configuración de ${getConfigLabel(config.key)}`).replace(/punto y coma/gi, 'comas')}
                          </p>
                        </div>
                        <button
                          onClick={() => handleSave(config)}
                          disabled={saving[config.key] || editedValues[config.key] === (config.value || '')}
                          className={`${btnPri} shrink-0`}
                        >
                          {saving[config.key] ? (
                            <>
                              <Loader2 className="animate-spin" size={16} />
                              Guardando...
                            </>
                          ) : (
                            <>
                              <Save size={16} />
                              Guardar
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No hay configuraciones disponibles. Las configuraciones se crearán automáticamente al iniciar el servidor.
                  </div>
                )}
              </div>
            </div>
          </GradientCard>
        )}
      </div>
    </div>
  );
}
